import { useMemo } from "react";
import {
  GOLD_ANNUAL_GROWTH_PCT,
  GOLD_PRICE_PER_GRAM_TODAY,
  NISAB_GOLD_GRAMS,
  ZAKAT_RATE,
  type ProjectionResult,
} from "@/lib/retirement-calc";

interface Props {
  result: ProjectionResult;
  fmtFull: Intl.NumberFormat;
  targetAge: number;
}

const ZakatBreakdown = ({ result, fmtFull, targetAge }: Props) => {
  const { rows, totalZakat, yearsBelowNisab, firstZakatRow } = useMemo(() => {
    // Only show years up to (and including) retirement age — post-retirement
    // Zakat depends on withdrawals + asset mix and isn't meaningful here.
    const r = result.rows.filter(
      (row) => row.yearsFromNow > 0 && row.age <= targetAge,
    );
    let total = 0;
    let below = 0;
    let first: typeof r[number] | undefined;
    for (const row of r) {
      total += row.zakatPaidNominal ?? 0;
      if (row.zakatApplied === false) below += 1;
      if (!first && row.zakatApplied) first = row;
    }
    return { rows: r, totalZakat: total, yearsBelowNisab: below, firstZakatRow: first };
  }, [result, targetAge]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <h2 className="text-lg font-bold">Zakat &amp; Nisab calculation</h2>
        <span className="text-xs text-muted-foreground">
          Nominal $ · accumulation years only
        </span>
      </div>

      <p className="text-sm text-foreground/80 leading-relaxed mb-3">
        Zakat ({(ZAKAT_RATE * 100).toFixed(2)}%/yr, the solar equivalent of 2.5% lunar)
        is only deducted in years where your portfolio reaches the{" "}
        <strong>Nisab threshold</strong> — the value of {NISAB_GOLD_GRAMS} g of gold. We
        assume gold starts at <strong>{fmtFull.format(GOLD_PRICE_PER_GRAM_TODAY)}/g</strong>{" "}
        today and appreciates <strong>{GOLD_ANNUAL_GROWTH_PCT}%/yr</strong>, so the
        threshold rises each year.
      </p>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mb-4 text-xs leading-relaxed text-foreground/80">
        <strong className="block text-amber-700 dark:text-amber-400 mb-1">
          This is the maximum Zakat estimate.
        </strong>
        Real-life Zakat must be recalculated each lunar year on what you actually own
        (cash, gold, silver, tradable stocks at market value, and business inventory).
        Long-term shares held purely for dividends, and operating business assets, are
        often zakatable on a different base (e.g. only the zakatable portion of the
        company), so your actual bill can be{" "}
        <strong>significantly lower</strong> than the figure shown.{" "}
        <a
          href="https://aaoifi.com/shariaa-standards/?lang=en"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline font-medium"
        >
          AAOIFI Shariah Standard No. 35 (Zakah)
        </a>{" "}
        and{" "}
        <a
          href="https://www.nzf.org.uk/knowledge/shares-investments/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline font-medium"
        >
          National Zakat Foundation guidance on shares
        </a>{" "}
        explain the asset-by-asset rules. Consult a qualified scholar for your own case.
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4 text-sm">
        <div className="rounded-lg bg-muted/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Nisab today
          </div>
          <div className="font-bold tabular-nums">
            {fmtFull.format(NISAB_GOLD_GRAMS * GOLD_PRICE_PER_GRAM_TODAY)}
          </div>
        </div>
        <div className="rounded-lg bg-muted/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Years below Nisab
          </div>
          <div className="font-bold tabular-nums">{yearsBelowNisab}</div>
        </div>
        <div className="rounded-lg bg-muted/40 p-3 col-span-2 sm:col-span-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Max Zakat to retirement
          </div>
          <div className="font-bold tabular-nums">{fmtFull.format(totalZakat)}</div>
        </div>
      </div>

      {firstZakatRow && (
        <p className="text-xs text-muted-foreground mb-3">
          First Zakat year: <strong>age {firstZakatRow.age}</strong> — portfolio{" "}
          {fmtFull.format(firstZakatRow.nominal)} ≥ Nisab{" "}
          {fmtFull.format(firstZakatRow.nisabNominal ?? 0)}.
        </p>
      )}

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs sm:text-sm border-separate border-spacing-0">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left font-semibold px-2 py-1.5">Age</th>
              <th className="text-right font-semibold px-2 py-1.5">Balance</th>
              <th className="text-right font-semibold px-2 py-1.5">Nisab</th>
              <th className="text-right font-semibold px-2 py-1.5">Zakat</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isRetire = row.age === targetAge;
              return (
                <tr
                  key={row.age}
                  className={`border-t border-border/60 ${
                    isRetire ? "bg-primary/5 font-semibold" : ""
                  }`}
                >
                  <td className="px-2 py-1.5 tabular-nums">
                    {row.age}
                    {isRetire && <span className="ml-1 text-[10px] text-primary">retire</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {fmtFull.format(row.nominal)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                    {fmtFull.format(row.nisabNominal ?? 0)}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums ${
                      row.zakatApplied ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {row.zakatApplied
                      ? `− ${fmtFull.format(row.zakatPaidNominal ?? 0)}`
                      : "— below Nisab"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ZakatBreakdown;
